// The trip between home and play as a small state machine (pure, tested
// without a DOM). Going up: the home UI and constellations fade while the
// drift settles (leave), the sky pans up with the level riding along (pan),
// the HUD fades in (arrive), then rest. Coming home mirrors it. A change of
// mind part way turns around from where it is.
export type Scene = 'home' | 'play';

export type ScenePhase =
  | { kind: 'rest'; at: Scene }
  | { kind: 'leave'; from: Scene; to: Scene }
  | { kind: 'pan'; from: Scene; to: Scene }
  | { kind: 'arrive'; at: Scene };

export type SceneEvent = { type: 'go'; to: Scene } | { type: 'step' };

export function sceneReducer(phase: ScenePhase, event: SceneEvent): ScenePhase {
  if (event.type === 'step') {
    switch (phase.kind) {
      case 'leave':
        return { kind: 'pan', from: phase.from, to: phase.to };
      case 'pan':
        return { kind: 'arrive', at: phase.to };
      case 'arrive':
        return { kind: 'rest', at: phase.at };
      default:
        return phase;
    }
  }
  const to = event.to;
  switch (phase.kind) {
    case 'rest':
    case 'arrive':
      return phase.at === to ? phase : { kind: 'leave', from: phase.at, to };
    case 'leave':
      // Not yet moving: fade the screen we were leaving back in.
      return phase.to === to ? phase : { kind: 'arrive', at: phase.from };
    case 'pan':
      // Mid-pan: turn around.
      return phase.to === to ? phase : { kind: 'pan', from: phase.to, to };
  }
}

/** Settled on a scene, or arriving at it. */
function settledAt(phase: ScenePhase): Scene | null {
  return phase.kind === 'rest' || phase.kind === 'arrive' ? phase.at : null;
}

/** The half the sky rests on outside a pan (a pan starts from it). */
export function restingHalf(phase: ScenePhase): Scene {
  return phase.kind === 'rest' || phase.kind === 'arrive' ? phase.at : phase.from;
}

/** Where the trip is headed. */
export function heading(phase: ScenePhase): Scene {
  return phase.kind === 'rest' || phase.kind === 'arrive' ? phase.at : phase.to;
}

/** A screen is in the page unless the trip is settled on the other one. */
export function mounted(phase: ScenePhase, scene: Scene): boolean {
  const at = settledAt(phase);
  return at === null || at === scene;
}

/** A screen's UI (home's cards and constellations, play's HUD) shows only once settled on it. */
export function uiShown(phase: ScenePhase, scene: Scene): boolean {
  return settledAt(phase) === scene;
}
